/**
 * Provably fair shuffling.
 *
 * The problem this solves is not that the shuffle might be biased — a CSPRNG
 * settles that. It is that players have no way to know. Every poker room in
 * history has been accused of rigging its deck, and "trust our audit" is a
 * weak answer. Commit-reveal turns it into something a player can check
 * themselves, with arithmetic, after the fact.
 *
 * How a hand works:
 *
 *   1. Before dealing, the server generates a 32-byte `serverSeed` and
 *      publishes `commitment = SHA-256(serverSeed)`. The seed itself stays
 *      secret; the commitment binds the server to it.
 *   2. Players may supply a `clientSeed` of their own. They do not have to —
 *      but a player who does knows the deck could not have been chosen to suit
 *      the server, because the server committed before seeing their seed.
 *   3. The deck is derived from those inputs alone.
 *   4. When the hand ends the server reveals `serverSeed`. Anyone can hash it
 *      to check it matches the commitment, then rerun the derivation and get
 *      the same deck, card for card.
 *
 * A server that wanted to cheat would have to find a second seed with the same
 * SHA-256 — and would still have to do it before knowing the client seeds.
 *
 * Building this now costs almost nothing. Retrofitting it later means changing
 * the shape of every stored hand.
 *
 * The derivation is deliberately simple so anyone can reimplement it in a few
 * lines and check our answer. It is specified exactly in `deckMessage` below.
 * Web Crypto is used rather than `node:crypto` so the same code runs unchanged
 * in a browser, which is where the verification tool lives.
 */

/** A card, 0..51, in the same encoding the engine uses: `(rank - 2) * 4 + suit`. */
export type Card = number;

export const DECK_SIZE = 52;
export const SERVER_SEED_BYTES = 32;

export interface ClientSeed {
  readonly seat: number;
  readonly seed: string;
}

export interface ShuffleInput {
  /** Secret until the hand ends. */
  readonly serverSeed: Uint8Array;
  /** Unique per hand. Stops one revealed seed from explaining two decks. */
  readonly handId: string;
  /** Optional player contributions. Order does not matter; they are sorted by seat. */
  readonly clientSeeds?: readonly ClientSeed[];
}

const encoder = new TextEncoder();

function subtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (c?.subtle === undefined) {
    throw new Error('Web Crypto is unavailable; @pf/fairness needs crypto.subtle');
  }
  return c.subtle;
}

export function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const byte of bytes) out += byte.toString(16).padStart(2, '0');
  return out;
}

export function fromHex(hex: string): Uint8Array {
  if (hex.length % 2 !== 0 || /[^0-9a-fA-F]/.test(hex)) {
    throw new Error(`not a hex string: ${JSON.stringify(hex)}`);
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return bytes;
}

/**
 * A fresh server seed. The only place this package produces randomness.
 *
 * Everything else is a pure function of its inputs, which is what makes a hand
 * reproducible from its event log.
 */
export function randomServerSeed(): Uint8Array {
  const seed = new Uint8Array(SERVER_SEED_BYTES);
  globalThis.crypto.getRandomValues(seed);
  return seed;
}

/** `SHA-256(serverSeed)` as hex — published before the deal, checked after the reveal. */
export async function commitment(serverSeed: Uint8Array): Promise<string> {
  const digest = await subtle().digest('SHA-256', serverSeed as BufferSource);
  return toHex(new Uint8Array(digest));
}

/**
 * The exact string the shuffle stream is keyed over, for block `counter`:
 *
 *     <handId>|<seat>:<seed>,<seat>:<seed>|<counter>
 *
 * Client seeds are sorted by seat so the same set always produces the same
 * string. This is the whole specification — a verifier needs nothing else.
 */
export function deckMessage(handId: string, clientSeeds: readonly ClientSeed[], counter: number): string {
  const seeds = [...clientSeeds]
    .sort((a, b) => a.seat - b.seat)
    .map((c) => `${c.seat}:${c.seed}`)
    .join(',');
  return `${handId}|${seeds}|${counter}`;
}

/** HMAC-SHA256 blocks keyed by the server seed, pulled one byte at a time. */
async function* byteStream(input: ShuffleInput): AsyncGenerator<number, never> {
  const key = await subtle().importKey(
    'raw',
    input.serverSeed as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const clientSeeds = input.clientSeeds ?? [];

  for (let counter = 0; ; counter++) {
    const message = encoder.encode(deckMessage(input.handId, clientSeeds, counter));
    const block = new Uint8Array(await subtle().sign('HMAC', key, message as BufferSource));
    for (const byte of block) yield byte;
  }
}

/**
 * Derive the shuffled deck.
 *
 * Fisher-Yates, with each index drawn by rejection sampling over a 32-bit
 * window. Rejection rather than modulo: `value % n` quietly favours the low
 * indices whenever `n` does not divide 2^32, which for a 52-card deck would
 * mean some cards really are dealt more often than others. The bias is small
 * enough that nobody would notice it and large enough to be a genuine defect.
 */
export async function deriveDeck(input: ShuffleInput): Promise<Card[]> {
  if (input.serverSeed.length === 0) throw new Error('serverSeed must not be empty');
  if (input.handId.length === 0) throw new Error('handId must not be empty');

  const stream = byteStream(input);
  const nextByte = async (): Promise<number> => (await stream.next()).value;

  const nextIndex = async (bound: number): Promise<number> => {
    // Largest multiple of `bound` that fits in 32 bits; anything at or above it
    // is discarded so every index stays equally likely.
    const limit = Math.floor(0x1_0000_0000 / bound) * bound;
    for (;;) {
      const value =
        (await nextByte()) * 0x100_0000 +
        (await nextByte()) * 0x1_0000 +
        (await nextByte()) * 0x100 +
        (await nextByte());
      if (value < limit) return value % bound;
    }
  };

  const deck: Card[] = Array.from({ length: DECK_SIZE }, (_, i) => i);
  for (let i = DECK_SIZE - 1; i > 0; i--) {
    const j = await nextIndex(i + 1);
    const tmp = deck[i]!;
    deck[i] = deck[j]!;
    deck[j] = tmp;
  }
  return deck;
}

export interface VerificationResult {
  readonly ok: boolean;
  /** The revealed seed hashes to the commitment the server published. */
  readonly commitmentMatches: boolean;
  /** Rerunning the derivation reproduces the deck the hand was actually dealt from. */
  readonly deckMatches: boolean;
  readonly expectedCommitment: string;
  readonly actualCommitment: string;
  readonly deck: readonly Card[];
}

/**
 * Check a finished hand.
 *
 * This is what a player runs. Both halves matter and they fail differently: a
 * commitment mismatch means the server revealed a different seed than the one
 * it committed to, while a deck mismatch means the seed is genuine but the
 * cards did not come from it.
 */
export async function verifyHand(options: {
  readonly serverSeed: Uint8Array;
  readonly handId: string;
  readonly clientSeeds?: readonly ClientSeed[];
  readonly publishedCommitment: string;
  readonly dealtDeck: readonly Card[];
}): Promise<VerificationResult> {
  const actualCommitment = await commitment(options.serverSeed);
  const deck = await deriveDeck({
    serverSeed: options.serverSeed,
    handId: options.handId,
    ...(options.clientSeeds === undefined ? {} : { clientSeeds: options.clientSeeds }),
  });

  const commitmentMatches = actualCommitment === options.publishedCommitment.toLowerCase();
  const deckMatches =
    deck.length === options.dealtDeck.length && deck.every((card, i) => card === options.dealtDeck[i]);

  return {
    ok: commitmentMatches && deckMatches,
    commitmentMatches,
    deckMatches,
    expectedCommitment: options.publishedCommitment.toLowerCase(),
    actualCommitment,
    deck,
  };
}
